import { formatDate } from "@/lib/api";

// Approx WPM for reading-time. Uses description + body text if provided.
const WPM = 235;
export function readingTime(text = "") {
  const words = String(text || "").split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / WPM));
}

// Maps a free-form category string to a `cat-badge--<slug>` variant.
export function categoryVariant(raw) {
  const s = String(raw || "").toLowerCase();
  if (s.includes("breaking")) return "breaking";
  if (s.includes("rumor")) return "rumors";
  if (s.includes("injur")) return "injuries";
  if (s.includes("practice")) return "practice";
  if (s.includes("film")) return "film";
  if (s.includes("signing") || s.includes("transaction") || s.includes("roster")) return "signings";
  if (s.includes("analysis") || s.includes("opinion") || s.includes("column")) return "analysis";
  return "story";
}

export function CategoryBadge({ category, className = "" }) {
  const variant = categoryVariant(category);
  return (
    <span
      data-testid={`cat-badge-${variant}`}
      className={`cat-badge cat-badge--${variant} ${className}`}
    >
      {category || "Story"}
    </span>
  );
}

/**
 * Compact meta strip: CATEGORY • AUTHOR • DATE • X MIN READ • VIEWS
 * `article`: { category, author, published, body, description, view_count,
 *              reading_time_min, updated_at }
 *
 * Reading time uses the backend-computed `reading_time_min` when available
 * (accounts for structured blocks); otherwise falls back to a WPM count of
 * description + body.
 */
export default function ArticleMeta({ article, showBadge = true, size = "sm", className = "" }) {
  if (!article) return null;
  const mins = Number.isFinite(article.reading_time_min)
    ? article.reading_time_min
    : readingTime(`${article.description || ""} ${article.body || ""}`);
  const date = article.published ? formatDate(article.published) : null;
  const views = Number(article.view_count || 0);
  const textCls = size === "xs" ? "text-[10px]" : "text-[11px]";
  const fmtViews = (n) => {
    if (n >= 10000) return `${(n / 1000).toFixed(0)}K`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return String(n);
  };
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid="article-meta">
      {showBadge && <CategoryBadge category={article.category} />}
      <span className={`meta-strip ${textCls}`}>
        {article.author && <span data-testid="meta-author">{article.author}</span>}
        {article.author && (date || mins) && <span className="dot" />}
        {date && <span data-testid="meta-date">{date}</span>}
        {(date || article.author) && <span className="dot" />}
        <span data-testid="meta-read">{mins} min read</span>
        {views > 0 && (
          <>
            <span className="dot" />
            <span data-testid="meta-views">{fmtViews(views)} reads</span>
          </>
        )}
      </span>
    </div>
  );
}
